import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import APIService from "../services/APIService";
import { formatDateTime } from "../utils/DateTimeUtil";
import "./css/SimulationLanding.css";

const SimulationLandingSMS = () => {
  const { shortCode } = useParams();
  const [messageUuid, setMessageUuid] = useState("");
  const [originalUrl, setOriginalUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clickData, setClickData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const trackClick = async () => {
      // Validate shortCode format before making API call
      if (!shortCode || !shortCode.startsWith("m") || shortCode.length !== 19) {
        setError("Invalid link format");
        setLoading(false);
        return;
      }

      // Extract message UUID from short code (e.g., "mABC123XYZ456789012" -> "ABC123XYZ456789012")
      const uuid = shortCode.substring(1);
      setMessageUuid(uuid);

      console.log("SMS simulation click detected for message UUID:", uuid);

      // Call backend to validate UUID and log the click
      try {
        const endpoint = `/api/v1/simulation/sms-click/${shortCode}`;

        const data = await APIService.get(endpoint);

        console.log("SMS click tracked successfully:", data);

        // Backend validated the UUID - it exists
        if (data && data.success !== false) {
          setClickData(data);
          setIsValid(true);
          // Use originalUrl from backend (extracted from message content)
          setOriginalUrl(data.originalUrl || "N/A");
          console.log("Original URL from backend:", data.originalUrl);
        } else {
          setError("This link is not valid");
        }
      } catch (error) {
        console.error("Error tracking SMS click:", error);

        // Handle specific error cases
        if (error.response?.status === 404) {
          setError("This link is not valid or has expired");
        } else if (error.response?.status === 400) {
          setError("Invalid link format");
        } else {
          setError("Unable to verify this link. Please try again later.");
        }
      } finally {
        setLoading(false);
        setCurrentTime(new Date());
      }
    };

    trackClick();
  }, [shortCode]);

  // Navigate to a specific section in the main app via URL-based deep linking
  const navigateToApp = (contentName) => {
    // Map content names to URL slugs for deep linking
    const contentToSlug = {
      "Training Center": "training",
      "Alerts Intelligence Hub": "alerts-hub",
      Dashboard: "dashboard",
    };
    const slug = contentToSlug[contentName] || "dashboard";
    window.location.href = `/app/${slug}`;
  };

  // Use centralized DateTimeUtil for consistent time formatting
  const formatTime = (date) => {
    return formatDateTime(date);
  };

  // Show loading state while validating UUID
  if (loading) {
    return (
      <div className="simulation-landing">
        <div className="simulation-container">
          <div className="simulation-header">
            <div className="warning-icon">
              <img
                src="/KinShield.png"
                alt="KinShield Logo"
                className="simulation-logo"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
            <h1>KinShield™ Protection Alert</h1>
          </div>
          <div className="simulation-content">
            <div className="detection-info">
              <h2>Validating link...</h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if UUID is invalid
  if (error || !isValid) {
    return (
      <div className="simulation-landing">
        <div className="simulation-container">
          <div className="simulation-header">
            <div className="warning-icon">
              <img
                src="/KinShield.png"
                alt="KinShield Logo"
                className="simulation-logo"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
            <h1>KinShield™ Protection Alert</h1>
          </div>
          <div className="simulation-content">
            <div className="detection-info">
              <h2>⚠️ Invalid Link</h2>
            </div>
            <div className="what-happened">
              <h3>Unable to Verify This Link</h3>
              <p className="explanation">
                {error || "This link is not valid or has expired."}
              </p>
              <p className="explanation">
                If you believe this is an error, please contact your KinShield
                administrator.
              </p>
            </div>
            <div className="action-buttons">
              <button
                className="btn-primary"
                onClick={() => (window.location.href = "/")}
              >
                Return to KinShield Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="simulation-landing">
      <div className="simulation-container">
        {/* Header Section */}
        <div className="simulation-header">
          <div className="warning-icon">
            <img
              src="/KinShield.png"
              alt="KinShield Logo"
              className="simulation-logo"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
          <h1>KinShield™ Protection Alert</h1>
          <div className="alert-banner">
            <strong>YOU ARE SAFE!</strong>
          </div>
        </div>

        {/* Main Content */}
        <div className="simulation-content">
          <div className="detection-info">
            <h2>
              🚨 This was a safe SMS phishing simulation for training purposes.
            </h2>
          </div>

          {/* What Happened Section */}
          <div className="what-happened">
            <h3>💡 What Just Happened?</h3>
            <p className="explanation">
              You just clicked on a link in a text message—and in a real attack,
              that could have had serious consequences. But don't worry! This
              was a <strong>safe training exercise</strong> designed to help you
              recognize SMS phishing (called "smishing") before it happens to
              you for real.
            </p>
            {originalUrl && originalUrl !== "N/A" && (
              <div className="url-reveal">
                <p className="url-label">The link you clicked:</p>
                <strong className="url-display">{originalUrl}</strong>
              </div>
            )}
          </div>

          {/* Why This Matters */}
          <div className="why-matters">
            <h3>⚠️ Why This Matters</h3>
            <p>
              If this had been a real attack instead of a training simulation,
              clicking that link could have:
            </p>
            <ul>
              <li>Installed malware on your phone that steals your data</li>
              <li>
                Captured your passwords, banking info, or personal details
              </li>
              <li>Subscribed you to expensive premium services</li>
              <li>Spread malicious links to everyone in your contacts</li>
            </ul>
            <p className="reassurance">
              <strong>The good news:</strong> You're now more aware, and
              awareness is your best defense!
            </p>
          </div>

          {/* Educational Section */}
          <div className="micro-lesson">
            <h3>🎓 How to Spot SMS Scams</h3>

            <div className="lesson-content">
              <div className="warning-signs">
                <h4>🚩 Red Flags to Watch For:</h4>
                <ul>
                  <li>
                    <strong>Creates urgency:</strong> "Your account will be
                    closed!" or "Act within 24 hours!"
                  </li>
                  <li>
                    <strong>Too good to be true:</strong> Surprise prizes, free
                    gifts, or unexpected refunds
                  </li>
                  <li>
                    <strong>Suspicious links:</strong> Shortened URLs (bit.ly,
                    tinyurl) that hide the real destination
                  </li>
                  <li>
                    <strong>Unknown sender:</strong> Messages from random
                    numbers claiming to be your bank or a trusted service
                  </li>
                  <li>
                    <strong>Requests sensitive info:</strong> Asking for
                    passwords, Social Security numbers, or payment details
                  </li>
                </ul>
              </div>

              <div className="safety-tips">
                <h4>✅ What to Do Instead:</h4>
                <ul>
                  <li>
                    <strong>Pause before clicking:</strong> Take a moment to
                    evaluate unexpected messages
                  </li>
                  <li>
                    <strong>Verify independently:</strong> Scammers are experts
                    at making texts look legitimate — even if you just booked a
                    flight, that "airline" text could still be fake. Always
                    contact the company directly using their official phone
                    number or website, not links in messages.
                  </li>
                  <li>
                    <strong>Don't respond:</strong> Replying confirms your
                    number is active and invites more scams
                  </li>
                  <li>
                    <strong>Report spam:</strong> Forward suspicious texts to
                    7726 (SPAM) and block the sender
                  </li>
                  <li>
                    <strong>Trust your instincts:</strong> If something feels
                    off, it probably is
                  </li>
                </ul>
              </div>

              <div className="device-protection">
                <h4>🛡️ Additional Protection Tips:</h4>
                <ul>
                  <li>Keep your phone's operating system and apps updated</li>
                  <li>Install security software on your mobile device</li>
                  <li>Enable spam blocking features in your phone settings</li>
                  <li>Only download apps from official app stores</li>
                  <li>Review your phone bill regularly for unusual charges</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Encouragement Section */}
          <div className="encouragement">
            <h3>🌟 You're Building Better Habits</h3>
            <p>
              Every training exercise like this one strengthens your ability to
              recognize threats. The fact that you're seeing this page means
              you're actively learning and improving your security awareness.
              Next time you receive a suspicious text, you'll be more prepared
              to spot the warning signs and protect yourself.
            </p>
            <p className="final-tip">
              <strong>Remember:</strong> When in doubt, don't click. Verify
              first, click later—or not at all.
            </p>
          </div>

          {/* Next Steps */}
          <div className="next-steps">
            <h3>📚 What's Next?</h3>
            <p>
              Take your awareness to the next level with targeted training and
              real-time security insights.
            </p>
            <div className="action-buttons">
              <button
                className="btn-primary"
                onClick={() => navigateToApp("Training Center")}
              >
                Start SMS Safety Training
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigateToApp("Alerts Intelligence Hub")}
              >
                View Security Alerts
              </button>
              <button
                className="btn-tertiary"
                onClick={() => (window.location.href = "/")}
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="simulation-footer">
          <p>
            This SMS simulation is part of KinShield's security awareness
            training program. Regular training helps build stronger security
            habits and protects against real threats via text messages.
          </p>
          <div className="timestamp">
            Simulation logged at: {formatTime(currentTime)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationLandingSMS;
